import React from 'react';
import { Field, Modal, Spinner } from '../../components/ui';
import CostLinesEditor from '../../components/costLines/CostLinesEditor';
import CostLinesBreakdown from '../../components/costLines/CostLinesBreakdown';
import { fmtDate, fmtTime, money } from '../../utils/helpers';
import { PAYMENT_MODES, OTHER_ITEM } from './useServiceDetail';
import { isPerUnit } from '../services/useServices';
import '../../styles/money.css';

// Desktop service detail: the engagement's facts, its dynamic lines (menu /
// jobs) and the payment ledger side by side. Each add-row is one line of
// fields sized to its content; Enter in it adds the line / payment.
export default function ServiceDetailDesktop({ vm }) {
  const {
    service, itemsByGroup, items, payments, loading, itemLabel, itemOptions,
    itemsTotal, paidTotal, canEdit, canMoney,
    itemForm, setItemF, addItem, savingItem, toggleItemStatus, removeItem,
    payForm, setPayF, addPayment, savingPay, removePayment, back,
    functions, fnName, costLines, itemised, liveTotal, liveBalance, liveMoney, discountForm, setDiscount, categories,
    linesForm, openLines, closeLines, setLine, addLine, removeLine, lineInfo, saveCostLines, savingLines, setDueDate,
  } = vm;

  // Enter in a box of an add-row = press its button (datalist boxes excluded).
  const enterTo = (fn, busy) => (e) => {
    if (e.key !== 'Enter' || e.defaultPrevented || busy) return;
    const t = e.target;
    if (t.tagName !== 'INPUT' || t.type === 'checkbox' || t.getAttribute('list')) return;
    e.preventDefault();
    fn();
  };

  if (loading) return <div className="card card-pad"><Spinner /></div>;
  if (!service) return <div className="card card-pad empty">Service not found. <button className="linklike" onClick={back}>Go back</button></div>;

  return (
    <div className="mn-sd">
      <div className="page-head">
        <h1>{service.ServiceType}</h1>
        <span className="muted">{service.CompanyName || service.ContactName || ''}</span>
        <div className="spacer" />
        <button className="btn light" onClick={back}>Back</button>
      </div>

      <div className="card card-pad" style={{ marginBottom: 14 }}>
        <div className="mn-facts">
          <div className="mn-fact"><span>Function</span><strong>{service.FunctionName || 'Whole event'}</strong></div>
          <div className="mn-fact"><span>Date</span><strong>{fmtDate(service.FunctionDate || service.ServiceDate) || '—'}</strong></div>
          <div className="mn-fact"><span>Time</span><strong>
            {fmtTime(service.StartTime || service.FunctionStartTime) || '—'}
            {(service.EndTime || service.FunctionEndTime) ? ` – ${fmtTime(service.EndTime || service.FunctionEndTime)}` : ''}
          </strong></div>
          <div className="mn-fact"><span>Venue</span><strong>{[service.Venue, service.City].filter(Boolean).join(', ') || '—'}</strong></div>
          <div className="mn-fact"><span>Owner</span><strong>{service.ContactName || service.VendorName || '—'}</strong>
            <div className="sub">{service.ContactMobile || service.VendorMobile || ''}</div></div>
          <div className="mn-fact"><span>Rate</span>
            {itemised ? <strong>Itemised · {costLines.length} lines</strong> : <strong>{money(service.Rate)}</strong>}
            {!itemised && isPerUnit(service.RateType) && <div className="sub">× {service.ConfirmedCount} {service.RateType === 'PerPlate' ? 'plates' : 'units'}</div>}</div>
          <div className="mn-fact"><span>Total / paid</span><strong>{money(liveTotal)}</strong>
            {/* A quotation's own sentence: quoted, less the discount, agreed. */}
            {liveMoney.discount > 0 && (
              <div className="sub">quoted {money(liveMoney.quoted)} − {money(liveMoney.discount)} off</div>
            )}
            {liveMoney.upTo > liveMoney.total && (
              <div className="sub">up to {money(liveMoney.upTo)} with the optional items</div>
            )}
            <div className="sub">paid {money(service.AdvancePaid)}</div></div>
          <div className="mn-fact"><span>Balance</span><strong style={{ color: 'var(--danger)' }}>{money(liveBalance)}</strong></div>
          {canMoney && (
            <div className="mn-fact"><span>Balance due by</span>
              <input type="date" value={String(service.DueDate || '').slice(0, 10)} onChange={e => setDueDate(e.target.value)} /></div>
          )}
        </div>
        {service.Notes && <div className="muted" style={{ marginTop: 8, fontSize: 13 }}>{service.Notes}</div>}
      </div>

      {canMoney && (
        <div className="card card-pad" style={{ marginBottom: 14 }}>
          <div className="mn-card-head">
            <strong>Charges</strong>
            <span className="muted" style={{ fontSize: 12 }}>
              {itemised
                ? `${costLines.length} lines · ${money(liveTotal)} (live)${liveMoney.upTo > liveMoney.total ? ` · up to ${money(liveMoney.upTo)}` : ''}`
                : 'One amount — itemise it into hall rent, plates, rooms …'}
            </span>
            <div className="spacer" />
            <button className="btn light sm" onClick={openLines}>{itemised ? '✎ Edit charges' : '+ Itemise charges'}</button>
          </div>
          <CostLinesBreakdown lines={costLines} fnName={fnName} />
        </div>
      )}

      {linesForm && (
        <Modal widest title={`Charges — ${service.CompanyName || service.ServiceType}`} onClose={closeLines}
          footer={<>
            {/* The discount belongs to the whole quotation, so it is typed here
                beside the total, not on a line. */}
            <label className="mn-sum" title="What the vendor took off the quoted price">
              Discount ₹
              <input className="mn-num w110" inputMode="decimal" value={discountForm} placeholder="0"
                onChange={e => setDiscount(e.target.value)} />
            </label>
            <span className="mn-sum">
              Quoted <strong>{money(linesForm.reduce((s2, l) => s2 + (lineInfo(l).optional ? 0 : lineInfo(l).amount), 0))}</strong>
              {Number(discountForm || 0) > 0 && (
                <> · agreed <strong>{money(linesForm.reduce((s2, l) => s2 + (lineInfo(l).optional ? 0 : lineInfo(l).amount), 0) - Number(discountForm || 0))}</strong></>
              )}
            </span>
            <button className="btn light" onClick={closeLines} disabled={savingLines}>Cancel</button>
            <button className="btn primary" onClick={saveCostLines} disabled={savingLines}>{savingLines ? 'Saving…' : 'Save'}</button>
          </>}>
          <CostLinesEditor lines={linesForm} setLine={setLine} addLine={addLine} removeLine={removeLine}
            lineInfo={lineInfo} functions={functions} categoryList="svc-line-categories" />
          <datalist id="svc-line-categories">{categories.map(c => <option key={c} value={c} />)}</datalist>
        </Modal>
      )}

      <div className="mn-sd-cols">
        {/* ── The dynamic lines: dishes for a caterer, jobs for a manager ── */}
        <div className="card card-pad">
          <div className="mn-card-head">
            <strong>{itemLabel} list</strong>
            <span className="muted" style={{ fontSize: 12 }}>{items.length} lines{itemsTotal ? ` · ${money(itemsTotal)}` : ''}</span>
          </div>

          {canEdit && (
            <div className="mn-line" onKeyDown={enterTo(addItem, savingItem)}>
              <div className="mn-grow">
                <Field label={`Add ${itemLabel.toLowerCase()}`} type="select" value={itemForm.ItemTypeId} options={itemOptions}
                  onChange={(v) => setItemF('ItemTypeId', v)} />
              </div>
              {itemForm.ItemTypeId === OTHER_ITEM && (
                <div className="mn-w180">
                  <Field label="New name" value={itemForm.ItemName} onChange={(v) => setItemF('ItemName', v)} />
                </div>
              )}
              <div className="mn-w80 mn-amt">
                <Field label="Qty" type="number" value={itemForm.Quantity} onChange={(v) => setItemF('Quantity', v)} />
              </div>
              <div className="mn-w100 mn-amt">
                <Field label="Rate (₹)" type="number" value={itemForm.Rate} onChange={(v) => setItemF('Rate', v)} />
              </div>
              <div className="mn-btn-cell">
                <button className="btn primary" disabled={savingItem} onClick={addItem}>{savingItem ? 'Adding…' : `+ Add ${itemLabel.toLowerCase()}`}</button>
              </div>
            </div>
          )}

          {items.length === 0 ? (
            <div className="empty" style={{ padding: 16 }}>Nothing added yet.</div>
          ) : itemsByGroup.map(({ group, list }) => (
            <div key={group || 'x'} style={{ marginTop: 10 }}>
              {group && <div className="step-label">{group}</div>}
              <table className="tbl compact">
                <tbody>
                  {list.map(it => (
                    <tr key={it.ServiceItemId}>
                      <td style={{ width: 34 }}>
                        <input type="checkbox" checked={it.Status === 'Done'} disabled={!canEdit}
                          onChange={() => toggleItemStatus(it)} title="Mark done" />
                      </td>
                      <td className="grow" style={{ textDecoration: it.Status === 'Done' ? 'line-through' : 'none' }}>
                        {it.ItemName}{it.Remarks ? <div className="sub">{it.Remarks}</div> : null}
                      </td>
                      <td className="num">{Number(it.Quantity) !== 1 ? it.Quantity : ''}</td>
                      <td className="mn-r">{Number(it.Amount) ? money(it.Amount) : ''}</td>
                      <td style={{ textAlign: 'right' }}>
                        {canEdit && <button className="icon-btn danger" title="Remove" onClick={() => removeItem(it)}>🗑</button>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>

        {/* ── Payments ── */}
        <div className="card card-pad">
          <div className="mn-card-head">
            <strong>Payments</strong>
            <span className="muted" style={{ fontSize: 12 }}>{money(paidTotal)} of {money(liveTotal)}</span>
          </div>
          {canMoney && (
            <div onKeyDown={enterTo(addPayment, savingPay)}>
              <div className="mn-line">
                <div className="mn-w150">
                  <Field label="Date" type="date" value={payForm.PaymentDate} onChange={(v) => setPayF('PaymentDate', v)} />
                </div>
                <div className="mn-w120 mn-amt">
                  <Field label="Amount (₹)" type="number" value={payForm.Amount} onChange={(v) => setPayF('Amount', v)} />
                </div>
                <div className="mn-grow">
                  <Field label="Mode" type="select" value={payForm.PaymentMode} options={PAYMENT_MODES} onChange={(v) => setPayF('PaymentMode', v)} />
                </div>
              </div>
              <div className="mn-line">
                <div className="mn-grow">
                  <Field label="Reference" value={payForm.Reference} onChange={(v) => setPayF('Reference', v)} />
                </div>
                <div className="mn-btn-cell">
                  <button className="btn primary" disabled={savingPay} onClick={addPayment}>{savingPay ? 'Saving…' : '+ Record payment'}</button>
                </div>
              </div>
            </div>
          )}
          {payments.length === 0 ? (
            <div className="empty" style={{ padding: 16 }}>No payments recorded.</div>
          ) : (
            <table className="tbl compact" style={{ marginTop: 8 }}>
              <tbody>
                {payments.map(p => (
                  <tr key={p.PaymentId}>
                    <td>{fmtDate(p.PaymentDate)}</td>
                    <td className="grow">{p.PaymentMode}{p.Reference ? <div className="sub">{p.Reference}</div> : null}</td>
                    <td className="mn-r"><strong>{money(p.Amount)}</strong></td>
                    <td style={{ textAlign: 'right' }}>
                      {canMoney && <button className="icon-btn danger" title="Remove" onClick={() => removePayment(p)}>🗑</button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <div className="mn-balance">
            <span className="muted">Balance</span><strong>{money(liveBalance)}</strong>
          </div>
        </div>
      </div>
    </div>
  );
}
